import { useEffect, useRef } from 'react';
import type { ReactElement, ReactNode } from 'react';

/* A side panel for the detail of a selected row or node.
 *
 * **A drawer rather than a modal, and rather than a route.** The detail of a lineage node or a
 * violation is read *against* the list it came from — a modal hides that context and a route loses the
 * reader's place in a long table. This is the progressive-disclosure affordance the brief asks for,
 * and the reason the screens it serves do not need a second page each.
 *
 * Three things it does that a `<div>` with `position: fixed` does not, and each of them is the reason
 * this is a component:
 *
 * * **Escape closes it.** Bound on the document, so it works wherever focus happens to be.
 * * **Focus moves in and comes back out.** The close button is focused on open and the previously
 *   focused element is restored on close, so a keyboard user who opened the drawer from row 40 of a
 *   table is returned to row 40 rather than to the top of the document.
 * * **The scrim is not the only way out.** Clicking it closes, and so does a labelled button, because
 *   a click-outside-to-dismiss affordance is invisible to anybody who cannot see it.
 *
 * Deliberately **not** a focus trap. A trap needs to enumerate every focusable descendant and gets it
 * wrong the moment a panel contains a `contenteditable` or an iframe; `aria-modal` plus the scrim is
 * what the pattern actually requires here, and the honest limitation is that Tab can reach the page
 * behind. For a non-blocking detail panel that is the correct trade — this drawer never asks a
 * question whose answer must be given before anything else happens. `Modal` is what does that.
 */

export interface DrawerProps {
  readonly title: string;
  readonly onClose: () => void;
  /** A subtitle or badge row under the title. */
  readonly meta?: ReactNode;
  readonly children: ReactNode;
  /** The button's accessible name, e.g. "Close the node detail". */
  readonly closeLabel: string;
}

export function Drawer({
  title,
  onClose,
  meta,
  children,
  closeLabel,
}: DrawerProps): ReactElement {
  const closeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const restoreTo = document.activeElement;
    closeRef.current?.focus();

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      /* Guarded on `isConnected`: the element that opened the drawer may itself have been unmounted
         by the interaction, and focusing a detached node silently moves focus to `<body>`, which
         drops a keyboard user at the top of the page. */
      if (restoreTo instanceof HTMLElement && restoreTo.isConnected) restoreTo.focus();
    };
  }, [onClose]);

  return (
    <>
      <div
        className="lq-drawer-scrim"
        onClick={onClose}
        /* Not a button and not focusable: it duplicates the labelled close control, and a
           focusable scrim is an unlabelled tab stop. */
        aria-hidden="true"
      />
      <aside className="lq-drawer" role="dialog" aria-modal="true" aria-label={title}>
        <header className="lq-drawer__header">
          <div style={{ minWidth: 0 }}>
            <h2 className="lq-drawer__title">{title}</h2>
            {meta !== undefined ? (
              <div style={{ marginTop: 'var(--space-2)' }}>{meta}</div>
            ) : null}
          </div>
          <button
            ref={closeRef}
            type="button"
            className="lq-icon-button"
            onClick={onClose}
            aria-label={closeLabel}
          >
            {/* A glyph rather than an icon font: no CDN reaches this deployment (hard rule 13), and a
                multiplication sign is unambiguous at any size. */}
            <span aria-hidden="true">✕</span>
          </button>
        </header>
        <div className="lq-drawer__body">{children}</div>
      </aside>
    </>
  );
}
